import { sortBy, groupBy, isObject, uniqBy } from 'lodash'

import { safeParseInt } from './safeParseInt'
import { positionReset } from './lists'

/**
 * @typedef {import('../index').Card} Card
 * @typedef {import('../index').Beat} Beat
 * @typedef {import('../index').Line} Line
 * @typedef {import('../index').Book} Book
 * @typedef {import('../index').AnySlateNode} AnySlateNode
 * @typedef {import('../index').HierarchyLevel} HierarchyLevel
 * @typedef {import('../index').PlottrFileBeats} PlottrFileBeats
 */

/**
 * @param {boolean} autoSort
 * @param {Array<Card>} cards
 * @param {Array<Line>} sortedLines
 * @return {Array<Card>}
 */
export function sortCardsInBeat(autoSort, cards, sortedLines) {
  if (autoSort) {
    // group by position within the line
    // for each position, sort those cards by the order of the lines
    const groupedCards = groupBy(cards, 'positionWithinLine')
    const sortedLineIds = sortedLines.map((l) => l.id)
    return Object.keys(groupedCards).flatMap((position) => {
      return groupedCards[position].sort(
        (a, b) => sortedLineIds.indexOf(a.lineId) - sortedLineIds.indexOf(b.lineId)
      )
    })
  } else {
    return sortBy(cards, 'positionInBeat')
  }
}

/**
 * @param {Array<Beat>} sortedBeats
 * @param {Array<Line>} sortedLines
 * @param {Record<String, Array<Card>>} card2Dmap
 * @param {Array<number> | Null} visibleLines
 * @return {Record<number, Array<Card>>}
 */
export function cardMapping(sortedBeats, sortedLines, card2Dmap, visibleLines) {
  /**
   * @type {Record<number, Array<Card>>}
   */
  const initialCardMapping = {}

  return sortedBeats.reduce((acc, beat) => {
    acc[beat.id] = sortedBeatCards(sortedLines, beat.id, card2Dmap, visibleLines)
    return acc
  }, initialCardMapping)
}

/**
 * @param {number} id
 * @param {Beat} beat
 * @param {Line} line
 * @return {Card}
 */
export function emptyCard(id, beat, line) {
  return {
    beatId: beat.id,
    bookId: beat.bookId,
    characters: [],
    color: null,
    description: [
      {
        children: [{ text: beat.title }],
        type: 'paragraph',
      },
    ],
    fromTemplateId: null,
    id,
    imageId: null,
    lineId: line.id,
    places: [],
    positionInBeat: 0,
    positionWithinLine: 0,
    tags: [],
    templates: [],
    title: beat.title,
    notes: [],
    audioClipId: null,
  }
}

/**
 * @param {Line} line
 * @param {Array<number> | Null} visibleLines
 * @return {boolean}
 */
function lineIsHidden(line, visibleLines) {
  return visibleLines === null || !visibleLines.includes(line.id)
}

/**
 * @param {Array<Line>} sortedLines
 * @param {number} beatId
 * @param {Record<String, Array<Card>>} card2Dmap
 * @param {Array<number> | Null} visibleLines
 * @return {Array<Card>}
 */
function sortedBeatCards(sortedLines, beatId, card2Dmap, visibleLines) {
  return sortedLines.reduce((acc, l) => {
    if (lineIsHidden(l, visibleLines)) return acc

    const cards = card2Dmap[`${l.id}-${beatId}`]
    if (cards) {
      return [...acc, ...cards]
    } else {
      return acc
    }
  }, [])
}

/**
 * @param {String} title
 * @param {number} maxLength
 * @return {String}
 */
export function truncateTitle(title, maxLength) {
  if (!title) return ''
  if (title.length < maxLength) return title
  return title.substr(0, maxLength) + '...'
}

/**
 * @param {Array<AnySlateNode> | String} children
 * @return {boolean}
 */
export const richContentIsNonEmpty = (children) => {
  if (typeof children === 'string') {
    return children !== ''
  }

  if (typeof children === 'undefined' || children === null) {
    return false
  }

  return (
    children.length > 1 ||
    children.some((element) => {
      return (
        ('text' in element && element.text && element.text !== '') ||
        ('children' in element &&
          Array.isArray(element.children) &&
          richContentIsNonEmpty(element.children))
      )
    })
  )
}

/**
 * @typedef TemplateDescriptor
 * @property {String} id
 * @property {String} attributeName
 */

/**
 * @typedef AttributeDescriptor
 * @property {String} [baseAttributeName]
 * @property {String} [customAttributeName]
 * @property {TemplateDescriptor} [template]
 */

/**
 * @param {String} rawCardId
 * @param {AttributeDescriptor} _param2
 * @return {Array<String | Number>}
 */
export const cardFocusPath = (
  rawCardId,
  { baseAttributeName, customAttributeName, template } = {
    baseAttributeName: undefined,
    customAttributeName: undefined,
    template: undefined,
  }
) => {
  const cardId = safeParseInt(rawCardId)
  if (typeof rawCardId !== 'number' && `${cardId}` !== rawCardId) {
    return ['unknown']
  } else if (isObject(template)) {
    const { id, attributeName } = template
    if (typeof id !== 'string' || typeof attributeName !== 'string') {
      return ['unknown']
    } else {
      return ['card', cardId, 'template', id, attributeName]
    }
  } else if (typeof customAttributeName === 'string') {
    return ['card', cardId, customAttributeName]
  } else if (baseAttributeName && ['title', 'description'].indexOf(baseAttributeName) !== -1) {
    return ['card', cardId, baseAttributeName]
  } else {
    return ['unknown']
  }
}

/**
 * @param {number} cardId
 * @param {String} hitType
 * @return {['plan' | 'fulltext' | 'legacy', 'card', number, 'title' | 'description'] | ['unknown']}
 */
export const outlineCardFocusPath = (cardId, hitType, view = 'plan') => {
  const lowercaseView = view.toLowerCase()
  // NOTE(ed): There are old tests that expect this.  Leaving the
  // check in in case this somehow happens in production.
  if (typeof cardId !== 'number') {
    return ['unknown']
  } else if (
    (lowercaseView === 'plan' || lowercaseView === 'fulltext' || lowercaseView === 'legacy') &&
    (hitType === 'title' || hitType === 'description')
  ) {
    return [lowercaseView, 'card', cardId, hitType]
  } else {
    return ['unknown']
  }
}

/**
 * @param {Array<any>} array
 * @param {String} key
 * @return {boolean}
 */
export const isUniqueByKey = (array, key) => {
  const uniqueArray = uniqBy(array, key)
  return uniqueArray.length === array.length
}

/**
 * @param {number} sourceId
 * @param {number} newPosition
 * @param {Array<Card>} list
 * @return {Array<Card>}
 */
export function moveCardToAbove(sourceId, newPosition, list, moveUp = true) {
  const newList = [...list]

  // Find the source index based on the source Id
  const sourcePosition = newList.findIndex((item) => item.id === sourceId)

  if (sourcePosition === -1 || newPosition < 0 || newPosition >= newList.length) {
    return newList
  }

  const [removed] = newList.splice(sourcePosition, 1)

  let insertIndex = newPosition
  if (moveUp) {
    insertIndex = newPosition === 0 ? 0 : newPosition - 1
  }

  newList.splice(insertIndex, 0, removed)

  return newList
}

/**
 * @param {number} newPosition
 * @param {Array<Card>} list
 * @param {Card} newItem
 * @param {boolean} moveUp
 * @return {Array<Card>}
 */
export function moveCardToPosition(newPosition, list, newItem, moveUp) {
  const newList = [...list]
  const targetPosition = moveUp ? newPosition : newPosition + 1

  newList.splice(targetPosition, 0, newItem)
  return positionReset(newList, 'positionInBeat')
}
